<template>
  <div class="content product-table-content">
    <div class="content__header">
      <h3>
        <span
          v-show="!isLoading && isCcrs && ccrsType === 'design'"
          v-tv:ccrs="{messageKey: 'resultsDesignHeader', messageValues: { total: total?.toLocaleString(i18n.locale.value === 'en' ? 'en' : 'de') }}"
        />
        <span
          v-show="!isLoading && isCcrs && ccrsType === 'product'"
          v-tv:ccrs="'ccrsProductHeader'"
        />
        <span
          v-show="!isLoading && !isCcrs"
          v-tv:categories="{messageKey: 'results', messageValues: { total: total?.toLocaleString(i18n.locale.value === 'en' ? 'en' : 'de') }}"
        />
        <Skeleton
          v-show="isLoading"
          shape="rectangle"
          class="content__header-skeleton"
        />
      </h3>
    </div>
    <div
      class="quantity-controls"
      v-show="!isLoading && showQuantityControls"
    >
      <div class="quantity-controls__wrapper">
        <div class="quantity-controls__input">
          <label
            for="quantity-input"
            class="quantity-controls__label"
          >
            {{ $tv('enterQuantityToSeePrices', 'product') }}
          </label>
          <NumericInput
            id="quantity-input"
            ref="quantityInput"
            v-model="quantity"
            :min="0"
            :max="1000000"
            :format="'n0'"
            :decimals="0"
            :spinners="true"
            :placeholder="$tv('enterQtyPlaceholder', 'product')"
            class="quantity-controls__numeric-input"
            new-style-with-spinner
            @keydown.enter="onCalculateClick"
          />
        </div>
        <ReactiveButton
          class="quantity-controls__calculate-btn button__secondary"
          :button-text="$tv('calculate', 'simmerringSelector')"
          :is-primary-button="false"
          @click="onCalculateClick"
          :disabled="isLoading"
        />
      </div>
    </div>

    <div
      class="grid custom-scrollbar"
      :class="{'grid--loading': isLoading}"
    >
      <div
        v-if="reducedProducts?.length > 0"
      >
        <div
          class="inch-value--filter"
          v-if="showInchToggle && !isCcrs && !isMobile"
          :class="{
            'inch-value--filter--no-quantity': !showQuantityControls,
            'inch-value--filter--with-quantity': showQuantityControls
          }"
        >
          <ToggleSwitchSquare
            class="product-table__toggle-switch"
            :is-checked="showInchValues"
            :label-right="$tv('metricInchToggleRightText','product')"
            :label-left="$tv('metricInchToggleLeftText','product')"
            @change-value="toggleShowInchValues"
          />
        </div>
        <div class="sort-by--mobile">
          <label
            class="sort-by--mobile__header"
            v-tv:categories="'sortBy'"
            for="sortBy"
            id="sortByLabel"
          />
          <div class="sort-by__dropdown__container">
            <DropDown
              id="sortBy"
              class="sort-by__dropdown"
              :data-items="sortByColumns"
              :text-field="'title'"
              :data-item-key="'field'"
              :value="sortByField"
              :popup-class-name="popupClassName"
              @change="sortByFieldChangeHandler"
              aria-labelledby="sortByLabel"
            />
          </div>
          <div class="sort-by__toggle-btn-group">
            <a
              class="k-button k-button-md k-button-solid k-button-solid-primary toggle-button"
              :class="{'inactive': sort?.length === 0 || sort[0].dir === 'desc'}"
              :disabled="sort?.length === 0"
              @click="changeSortOrder"
            >
              <NavigationArrowDropUp />
            </a>
            <a
              class="k-button k-button-md k-button-solid k-button-solid-primary toggle-button"
              :class="{'inactive': sort?.length === 0 || sort[0].dir === 'asc'}"
              :disabled="sort?.length === 0"
              @click="changeSortOrder"
            >
              <NavigationArrowDropDown />
            </a>
          </div>
          <div
            class="sort-by__toggle-btn-group--mobile"
            @click="changeSortOrder"
          >
            <a
              class="toggle-button-up"
              :class="{'inactive': sort?.length === 0 || sort[0].dir === 'asc'}"
            >
              <NavigationArrowDropUp />
            </a>
            <a
              class="toggle-button-down"
              :class="{'inactive': sort?.length === 0 || sort[0].dir === 'desc'}"
            >
              <NavigationArrowDropDown />
            </a>
          </div>
        </div>
        <div
          v-if="isAnySearch"
          class="anysearch-warning"
        >
          <span>{{ $tv({messageKey: 'warningAnySearch', messageValues: {search: searchTerm} }, 'searchResults') }}</span>
        </div>
        <div
          class="grid-wrapper"
          :data-price-overlay-text="showQuantityControls ? $tv('enterQuantityToSeePrices', 'product') : null"
          :class="{ 'show-price-overlay': showQuantityControls && !hasCalculatedPrices, 'single-row': reducedProducts?.length === 1 }"
        >
          <Grid
            :key="gridKey"
            :sortable="sortable"
            :sort="sort"
            @sortchange="sortChangeHandler"
            :pageable="pageable"
            @pagechange="pageChangeHandler"
            :skip="paginationOptions.skip"
            :take="paginationOptions.take"
            :total="total"
            :page-size="paginationOptions.take"
            :data-items="reducedProducts"
            :table-columns="tableColumns"
          >
            <template #linkSkuDescriptionCell="{props}">
              <td
                :data-th="getColumnTitle(props.field)"
                :class="props.className"
              >
                <NuxtLink
                  rel="nofollow"
                  :to="getDetailLinkUrl(getNestedValue('sku', props.dataItem))"
                  :target="openDetailInNewTab ? '_blank' : undefined"
                  @click="trackProductClick(props.dataItem, props.dataIndex)"
                  @mousedown="saveQuantityBeforeNavigation(props.dataItem)"
                >
                  <SKUContainer
                    class="sku-field"
                    :product="props.dataItem"
                  />
                  <span v-text="getDescriptionText(props.field, props.dataItem)" />
                </NuxtLink>

                <div
                  class="successor-item-container"
                  v-if="getSuccessorSku(props.dataItem) !== null"
                >
                  <NuxtLink
                    rel="nofollow"
                    :to="getDetailLinkUrl(getSuccessorSku(props.dataItem))"
                    :target="openDetailInNewTab ? '_blank' : undefined"
                  >
                    <p class="successor-item-container__successor-item">
                      {{ $tv('successorItem','product') }}
                    </p>
                  </NuxtLink>
                  <ImageCircle
                    v-if="hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) && !loadingIndicators.successorAvailabilities.some((s) => s == getSuccessorSku(props.dataItem)) && isSuccessorAvailable(props.dataItem)"
                    class="svg-image-circle icon--svg circle--available"
                  />
                  <OutlineCircle
                    v-else-if="hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)"
                    class="svg-outline-circle icon--svg circle--not-available"
                  />
                </div>
              </td>
            </template>
            <template #linkSkuCell="{props}">
              <td
                :data-th="getColumnTitle(props.field)"
                :class="props.className"
              >
                <NuxtLink
                  rel="nofollow"
                  :to="getDetailLinkUrl(getNestedValue('sku', props.dataItem))"
                  :target="openDetailInNewTab ? '_blank' : undefined"
                  @click="trackProductClick(props.dataItem, props.dataIndex)"
                  @mousedown="saveQuantityBeforeNavigation(props.dataItem)"
                >
                  <SKUContainer
                    :product="props.dataItem"
                    :multiple-lines="true"
                  />
                </NuxtLink>
                <div
                  class="successor-item-container"
                  v-if="getSuccessorSku(props.dataItem) !== null"
                >
                  <NuxtLink
                    rel="nofollow"
                    :to="getDetailLinkUrl(getSuccessorSku(props.dataItem))"
                    :target="openDetailInNewTab ? '_blank' : undefined"
                  >
                    <p class="successor-item-container__successor-item">
                      {{ $tv('successorItem','product') }}
                    </p>
                  </NuxtLink>
                  <ImageCircle
                    v-if="hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) && !loadingIndicators.successorAvailabilities.some((s) => s == getSuccessorSku(props.dataItem)) && isSuccessorAvailable(props.dataItem)"
                    class="svg-image-circle icon--svg circle--available"
                  />
                  <OutlineCircle
                    v-else-if="hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)"
                    class="svg-outline-circle icon--svg circle--not-available"
                  />
                </div>
              </td>
            </template>
            <template #linkCell="{props}">
              <td
                :data-th="getColumnTitle(props.field)"
                :class="props.className"
              >
                <NuxtLink
                  rel="nofollow"
                  :to="getDetailLinkUrl(getNestedValue('sku', props.dataItem))"
                  :target="openDetailInNewTab ? '_blank' : undefined"
                  @click="trackProductClick(props.dataItem, props.dataIndex)"
                  @mousedown="saveQuantityBeforeNavigation(props.dataItem)"
                >
                  {{ getDescriptionText(props.field, props.dataItem) }}
                </NuxtLink>
              </td>
            </template>
            <template #packagingCell="{props}">
              <td
                :data-th="getColumnTitle(props.field)"
                class="packaging-column"
                :class="props.className"
              >
                <QuantityDisplay
                  class="variants-link"
                  v-if="$auth.loggedIn && (hasSuccessor(props.dataItem) || hasVariants(props.dataItem))"
                  @click="openPopup(props.dataItem)"
                  :quantity="props.dataItem.variant?.amount"
                  :unit="props.dataItem.variant?.unit"
                />
                <QuantityDisplay
                  class="packaging-quantity"
                  v-else
                  :quantity="props.dataItem.variant?.amount"
                  :unit="props.dataItem.variant?.unit"
                />
              </td>
            </template>
            <template #availableCell="{props}">
              <td
                :data-th="getColumnTitle(props.field)"
                class="availability-column"
                :class="props.className"
              >
                <div
                  v-if="!loadingIndicators.availabilities.some(s => s == props.dataItem?.sku)"
                  class="availability-skeleton"
                >
                  <Skeleton
                    shape="circle"
                    class="availability-skeleton__circle"
                  />
                  <Skeleton
                    shape="rectangle"
                    class="availability-skeleton__line"
                  />
                </div>
                <div
                  v-else
                >
                  <ImageCircle
                    v-if="hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) && !REQUEST_ONLY_STATUS.includes(props.dataItem.salesAreas[0].status) && isAvailable(props.dataItem)"
                    class="svg-image-circle icon--svg circle--available"
                  />
                  <OutlineCircle
                    v-else-if="hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) && !REQUEST_ONLY_STATUS.includes(props.dataItem.salesAreas[0].status)"
                    class="svg-outline-circle icon--svg circle--not-available"
                  />
                  <span
                    class="stock-quantity"
                    v-text="getCurrentStock(props.dataItem)"
                  />
                </div>
              </td>
            </template>
            <template #priceCell="{props}">
              <td
                :data-th="getColumnTitle(props.field)"
                class="price-column"
                :class="props.className"
              >
                <div
                  v-if="!loadingIndicators.prices.some(s => s == props.dataItem?.sku) && (showQuantityControls ? hasCalculatedPrices : true)"
                  class="price-skeleton"
                >
                  <Skeleton
                    shape="rectangle"
                    class="price-skeleton__heading"
                  />
                  <Skeleton
                    shape="rectangle"
                    class="price-skeleton__subtext"
                  />
                </div>
                <div v-else-if="showQuantityControls ? hasCalculatedPrices : true">
                  <Price
                    class="price-value"
                    :price="getPrice(props.dataItem)"
                  />
                  <span
                    v-if="getMininumPositionLineValue(props.dataItem)"
                    class="minimum-position-line-value"
                  >
                    <span
                      v-text="$tv('minPositionValueProductTable', 'product') + ' '"
                    />
                    <Price
                      :price="getMininumPositionLineValue(props.dataItem)"
                    />
                  </span>
                </div>
                <div
                  class="price-placeholder"
                  v-else-if="showQuantityControls && !hasCalculatedPrices"
                >
                  <span v-tv:product="'enterQtyPlaceholder'" />
                </div>
              </td>
            </template>
            <template #imgCell="{props}">
              <td
                :data-th="getColumnTitle(props.field)"
                class="img-column"
                :class="props.className"
              >
                <NuxtLink
                  :to="getDetailLinkUrl(getNestedValue('sku', props.dataItem))"
                  :target="openDetailInNewTab ? '_blank' : undefined"
                  @mousedown="saveQuantityBeforeNavigation(props.dataItem)"
                >
                  <img
                    :src="imageUrl(props.dataItem, true)"
                    :alt="$tv('designsketch', 'product')"
                    class="dimension-img"
                  >
                </NuxtLink>
              </td>
            </template>
            <template #actionCell="{props}">
              <td
                class="action-column"
                :class="props.className"
              >
                <div class="actions">
                  <!-- <a
                  :title="$tv('bookmarkIconTooltip', 'categories')"
                  class="star"
                  href="javascript:void(0)"
                  @click="actionEmitter('favoriteItem', $event, props.dataItem)"
                >
                  <ToggleStarOutline />
                </a> -->
                  <a
                    class="k-button k-button-md k-button-solid k-button-solid-base button--big-text button__details"
                    :href="getDetailLinkUrl(getNestedValue('sku', props.dataItem))"
                    :target="openDetailInNewTab ? '_blank' : undefined"
                    @mousedown="saveQuantityBeforeNavigation(props.dataItem)"
                    v-tv:categories="'details'"
                  />
                  <a
                    :title="$tv('shoppingCartIconTooltip', 'categories')"
                    class="shoppingCart"
                    :class="{disabled: !isBuyable(props.dataItem)}"
                    href="javascript:void(0)"
                    @click="isBuyable(props.dataItem) && actionEmitter('shopItem', $event, props.dataItem); clickItem(props.dataItem, props.dataIndex)"
                  >
                    <span
                      class="k-button k-button-md k-button-solid k-button-solid-primary button--big-text"
                      v-tv:categories="'addToCart'"
                    />
                    <ActionShoppingCart />
                  </a>
                </div>
              </td>
            </template>
          </Grid>
        </div>
      </div>
      <div
        v-if="!isLoading && reducedProducts?.length > 0"
        class="pagination-controls"
        :class="{ 'experimental-position': showQuantityControls }"
      >
        <DropDown
          class="k-dropdown--cat pagination-size-dropdown"
          :data-items="pageSizeDropdown"
          :text-field="'text'"
          :data-item-key="'take'"
          :value="pageSizeDropdownValue"
          @change="pageSizeChangeHandler"
        />
      </div>
      <div
        v-if="!isLoading && reducedProducts?.length <= 0"
        v-tv:categories="'noProductsFound'"
      />
    </div>
    <PopupSuccessorVariants
      :show="showVariants"
      :sku-array="variantsAndSuccessor"
      @close-variants="showVariants = false"
    />
  </div>
</template>
<script>
import {DEFAULT_VALUES} from '~/assets/js/filters'
import {useItemImageUrlHelper} from '~/composables/item_image_url_helper'
import {GRID_SETTINGS, USER_RIGHTS_MAPPING, PROPERTY_SCOPE_FIELDS, REGION_KEYS, REQUEST_ONLY_STATUS} from '~/assets/js/constants'
import useGrid from '~/composables/useGrid'
import {sanitizeHtml} from '~/assets/js/utils'
import {storeToRefs} from 'pinia'
import {getMappedProduct, getMappedProductNew, mapProductAttributeNames} from '~/assets/js/utils/product_attributes_mapping'
import {useUserStore} from '@/store/user'
import {useQuantityStore} from '@/store/quantity'
import Grid from '@/components/grid/Grid'
import DropDown from '@/components/input-components/DropDown.vue'
import NumericInput from '@/components/input-components/NumericInput.vue'
import ReactiveButton from '~/components/ReactiveButton'
import NavigationArrowDropUp from '~/assets/icons/navigation_arrow_drop_up.svg?component'
import NavigationArrowDropDown from '~/assets/icons/navigation_arrow_drop_down.svg?component'
import ImageCircle from '~/assets/icons/image_circle.svg?component'
import OutlineCircle from '~/assets/icons/outline_circle.svg?component'
import ActionShoppingCart from '~/assets/icons/action_shopping_cart_plus.svg?component'
import ToggleSwitchSquare from '~/components/input-components/ToggleSwitchSquare'
import Price from '~/components/PriceF'
import SKUContainer from '~/components/SKUContainer'
import {Skeleton} from '@progress/kendo-vue-indicators'

export default {
  components: {
    DropDown,
    Grid,
    NavigationArrowDropUp,
    NavigationArrowDropDown,
    ImageCircle,
    OutlineCircle,
    ActionShoppingCart,
    Skeleton,
    ToggleSwitchSquare,
    Price,
    SKUContainer,
    NumericInput,
    ReactiveButton
  },
  props: {
    products: {
      type: Array,
      required: true,
      default() {
        return []
      }
    },
    total: {
      type: Number,
      required: true,
      default() {
        return 0
      }
    },
    isMobile: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    columns: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    sort: {
      type: Array,
      required: true,
      default() {
        return []
      }
    },
    pagination: {
      type: Object,
      required: true,
      default() {
        return {
          skip: DEFAULT_VALUES.SKIP,
          take: DEFAULT_VALUES.TAKE
        }
      }
    },
    isLoading: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    isSearchResult: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    isMultipleCategories: {
      type: Boolean,
      required: false,
      default() {
        return true
      }
    },
    assetForProducts: {
      type: Array,
      default() {
        return []
      }
    },
    prices: {
      type: Array,
      default() {
        return []
      }
    },
    loadingIndicators: {
      type: Object,
      default() {
        return {}
      }
    },
    availabilities: {
      type: Array,
      default() {
        return []
      }
    },
    successorAvailabilities: {
      type: Array,
      default() {
        return []
      }
    },
    showDesignSketch: {
      type: Boolean,
      required: false,
      default() {
        return true
      }
    },
    listId: {
      type: String,
      default() {
        // return this.isSearchResult ? 'search_results' : 'category_products'
        return 'category_products'
      }
    },
    listName: {
      type: String,
      default() {
        // return this.isSearchResult ? 'Search Results' : 'Category Products'
        return 'Category Products'
      }
    },
    isDefaultCustomer: {
      type: Boolean,
      default() {
        return false
      }
    },
    isAnySearch: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    searchTerm: {
      type: String,
      required: false,
      default() {
        return ''
      }
    },
    isCcrs: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    ccrsType: {
      type: String,
      required: false,
      default() {
        return 'design'
      }
    },
    openDetailInNewTab: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    popupClassName: {
      type: String,
      default() {
        return ''
      }
    }
  },
  setup() {
    const {showInchValues, settings, rights} = storeToRefs(useUserStore())
    const {updateSettings, setShowInchValues} = useUserStore()
    const quantityStore = useQuantityStore()

    const {getColumnTitle: getColumnTitleFn, getNestedValue} = useGrid()
    const {imageUrl} = useItemImageUrlHelper()
    return {
      showInchValues,
      userSettings: settings,
      rights,
      updateSettings,
      setShowInchValues,
      quantityStore,
      i18n: useI18n(),
      getColumnTitleFn,
      getNestedValue,
      imageUrl
    }
  },
  computed: {
    showQuantityControls() {
      return this.$auth?.loggedIn
    },
    quantity: {
      get() {
        return this.quantityStore.getQuantity()
      },
      set(value) {
        if (value === '' || value === null || value === undefined) {
          this.quantityStore.saveQuantity(null)
        } else {
          const num = Number(value)
          if (!Number.isNaN(num) && num > 0) {
            this.quantityStore.saveQuantity(Math.max(1, Math.floor(num)))
          } else {
            this.quantityStore.saveQuantity(null)
          }
        }
      }
    },
    sortable() {
      return {
        allowUnsort: true,
        mode: 'single'
      }
    },
    paginationOptions() {
      return this.pagination
    },
    reducedProducts() {
      if (this.products) {
        this.products.forEach(product => {
          product.brandName = this.$tv(product?.brandName, 'brands')
          product.productDesignTechnical = product?.productDesign
          product.productDesign = this.$tv(product?.productDesign, 'categories', product?.productDesign)
          product.pricingFlag = this.$tv(product?.pricingFlag, 'categories')
          product.variant = getMappedProductNew(product)?.variant
        })
        this.products.map(product => ({...product, ...product.characteristics?.reduce((o, a) => {
          o[a.key] = a.valueDec !== null ? a.valueDec : a.value
          return o
        }, {})}))

        return this.products.map(product => ({...product, ...product.characteristics?.reduce((o, a) => {
          o[a.key] = a.valueDec !== null ? a.valueDec : a.value
          return o
        }, {})}))
      }
      else {
        return []
      }
    },
    sortByField: {
      get: function () {
        return this.sortByColumns.find(t => t.field === (this.sort[0]?.field ?? ''))
      },
      set: function (value) {
        let sort = []
        if (value.field) {
          const direction = this.sort[0]?.dir ?? 'asc'
          sort = [{dir: direction, field: value.field}]
        }
        this.sortByEmitter(sort)
      }
    },
    getPriceObject: function() {
      return (product) => {
        const productStatus = product?.salesAreas && product?.salesAreas?.length > 0 ? product?.salesAreas[0]?.status : null
        return this.hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_PRICE) && !REQUEST_ONLY_STATUS.includes(productStatus) ? this.prices.find(p => p?.sku === product?.sku) : null
      }
    },
    getPrice: function() {
      return (product) => this.getPriceObject(product)?.basePrice
    },
    getMininumPositionLineValue: function() {
      return (product) => this.getPriceObject(product)?.minimumOrderLineValue
    },
    getAvailability: function() {

      return (product) => {
        const productStatus = product?.salesAreas && product?.salesAreas.length > 0 ? product.salesAreas[0].status : null
        return this.hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) && !REQUEST_ONLY_STATUS.includes(productStatus) ? this.availabilities?.find(a => a?.sku === product?.sku) ?? null : null
      }
    },
    getSuccessorAvailability: function() {
      return (product) => {
        return this.hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) ? this.successorAvailabilities?.find(a =>
          a?.sku === product?.salesAreas[0]?.successorSku) ?? null : null
      }
    },
    getCurrentStock: function() {
      return (product) => {
        const availability = this.getAvailability(product)
        if (!this.hasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) || !availability?.availabilities) {
          return '-'
        }

        const totalStock = availability.availabilities
          .filter(a => !a.plantIsDisplayOnly)
          .reduce((sum, a) => sum + (a.currentStock?.quantity || 0), 0)

        const unit = availability.availabilities?.find(a => a.currentStock?.unit)?.currentStock?.unit

        return totalStock > 0 ? this.getQuantityDisplay(totalStock, unit) : '-'
      }
    },
    isAvailable: function() {
      return (product) => {
        const availability = this.getAvailability(product)
        if (!availability?.availabilities) {
          return false
        }
        const totalStock = availability.availabilities
          .filter(a => !a.plantIsDisplayOnly)
          .reduce((sum, a) => sum + (a.currentStock?.quantity || 0), 0)
        return totalStock > 0
      }
    },
    isSuccessorAvailable: function() {
      return (product) => {
        return this.getSuccessorAvailability(product)?.availabilities?.[0]?.currentStock?.quantity > 0
      }
    },
    showInchToggle() {
      return this.$globalization.__currentRegion === REGION_KEYS.NA
    },
    variantsAndSuccessor() {
      const variants = (this.$deproxy(this.variantProduct?.otherVariants) ?? []).map(v => ({...v, isSuccessor: false}))
      const successor = this.variantProduct?.salesAreas.map(s => {
        // eslint-disable-next-line eqeqeq
        return s.successorSku != null ? {sku: s.successorSku, isSuccessor: true} : null
      })
      let result = null
      if (successor && variants) {
        result = [{sku: this.variantProduct?.sku}, ...successor, ...variants]
      }
      else if (successor) {
        result = [{sku: this.variantProduct?.sku}, ...successor]
      }
      else if (variants) {
        result = [{sku: this.variantProduct?.sku}, ...variants]
      }
      else {
        result = [{sku: this.variantProduct?.sku}]
      }
      // eslint-disable-next-line eqeqeq
      return result.filter(vs => vs != null)
    }
  },
  data() {
    const pageSizes = [25, 50]
    const pageSizesDropDownValues = []
    let pageSizesDropDownValue = {}
    pageSizes.forEach(value => {
      const currentValue = {text: this.i18n.t('categories.entries', {take: value}), take: value}
      pageSizesDropDownValues.push(currentValue)
      if (value === this.pagination.take) {
        pageSizesDropDownValue = currentValue
      }
    })

    let right_dependent_columns = []

    const availabilityColumn = {
      field: 'pcs',
      title: this.i18n.t('categories.availability'),
      width: this.isSearchResult && this.isMultipleCategories ? '130' : '125',
      locked: true,
      cell: 'availableCell',
      sortable: false,
      className: 'availability-column'
    }

    const priceColumn = {
      field: 'price',
      title: this.i18n.t('product.price-per-100'),
      width: this.isSearchResult && this.isMultipleCategories ? '120' : '115',
      locked: true,
      cell: 'priceCell',
      sortable: false,
      className: 'price-column-header'
    }

    // need local instance of "hasRight" bcs. "rights" computed from mixin is not present at this moment
    const hasRightInternal = (right) => this.rights && this.rights.some((item) => item === right)

    if (this.$auth && this.$auth.loggedIn && !this.isDefaultCustomer) {
      if (hasRightInternal(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)) {
        right_dependent_columns.push(availabilityColumn)
      }

      if (hasRightInternal(USER_RIGHTS_MAPPING.PRODUCT_VIEW_PRICE)) {
        right_dependent_columns.push(priceColumn)
      }
    }

    let descriptionField = 'locales_' + this.i18n.locale.value.toUpperCase() + '_' + (this.showInchValues ? 'Imperial' : 'Metric')  + '_SalesTextECom'

    let tableColumns = this.isSearchResult && this.isMultipleCategories ?
      [
        {field: '', title: '', width: '35', locked: true, cell: 'imgCell', sortable: false},
        {field: descriptionField, title: this.i18n.t('categories.salestext'), width: '385', locked: true, cell: 'linkSkuDescriptionCell', sortable: false, className: 'description-column sku-description-column'},
        {field: 'productDesign', title: this.i18n.t('categories.design'), width: '105', cell: 'simpleCell'},
        {field: 'material1Name', title: this.i18n.t('categories.material'), width: '150', cell: 'simpleCell'},
        {field: 'brand', title: this.i18n.t('categories.brand'), width: '150', cell: 'simpleCell', className: 'brand-column'},
        {...GRID_SETTINGS.EMPTY_CELL},
        {field: 'packaging', title: this.i18n.t('product.packaging'), width: '100', locked: true, cell: 'packagingCell', sortable: false, className: 'packaging-column'},
        ...right_dependent_columns,
        {field: '', title: '', width: '45', locked: true, cell: 'actionCell', sortable: false}
      ]
      :
      [
        {field: '', title: '', width: '35', locked: true, cell: 'imgCell', sortable: false},
        {field: 'skuNumeric', title: this.i18n.t('categories.sku'), width: '110', locked: true, cell: 'linkSkuCell', className: 'link-column'},
        {field: 'dynamicColumns'},
        {field: 'packaging', title: this.i18n.t('product.packaging'), width: '100', locked: true, cell: 'packagingCell', sortable: false, className: 'packaging-column'},
        ...right_dependent_columns,
        {field: '', title: '', width: '45', locked: true, cell: 'actionCell', sortable: false},
        {field: descriptionField, title: '', width: '0', cell: 'linkCell', sortable: false, className: 'description-column hide-desktop link-column'}
      ]
    if (!this.showDesignSketch) {
      tableColumns.shift()
    }

    return {
      tableColumns,
      origTableColumns: [...tableColumns],
      sortByColumns: [],
      skip: this.pagination.skip,
      take: this.pagination.take,
      pageable: {
        buttonCount: 3,
        info: false,
        type: 'numeric',
        previousNext: true,
        pageSizes: false
      },
      isFirstSort: true,
      pageSizeDropdown: pageSizesDropDownValues,
      pageSizeDropdownValue: pageSizesDropDownValue,
      gridKey: 0,
      successor: '',
      REQUEST_ONLY_STATUS: REQUEST_ONLY_STATUS,
      showVariants: false,
      variantProduct: null,
      hasCalculatedPrices: !this.$auth?.loggedIn
    }
  },
  methods: {
    getColumnTitle(fieldName) {
      return this.getColumnTitleFn(this.tableColumns, fieldName)
    },
    hasSuccessor(product) {
      return product?.salesAreas[0]?.successorSku !== null
    },
    hasVariants(product) {
      return product?.otherVariants?.length > 0
    },
    openPopup(product) {
      this.variantProduct = product
      this.showVariants = !this.showVariants
    },
    cleanColumnData() {
      let dynColIndex = this.origTableColumns.findIndex(column => column.field === 'dynamicColumns')
      if (dynColIndex >= 0) {
        let tableColumnsCopy = [...this.origTableColumns]
        tableColumnsCopy.splice(dynColIndex, 1)
        let columns = [...this.columns].filter(c => c.ShowInProductTable).sort((x, y) => (x.ProductTableSortOrder ?? 999999999) - (y.ProductTableSortOrder ?? 999999999))
        let insertIndex = dynColIndex
        let newColumns = []
        columns.forEach(column => {
          let title = column.ShortLabel ?? this.$tv(column.Attribute.TechnicalName, 'attributesShort', 'shortNameNotAvailable')
          let headerTitle = column.Label ?? this.$tv(column.Attribute.TechnicalName, 'attributes')
          if (title === 'shortNameNotAvailable' || title === headerTitle || title === '') {
            title = headerTitle
            headerTitle = null
          }
          if (column.Attribute.UnitOfMeasure) {
            const lowerUnitOfMeasure = column.Attribute.UnitOfMeasure.toLowerCase()
            const translatedUnitOfMeasure = this.showInchValues && lowerUnitOfMeasure === 'mm' ?
              this.$tv('inch', 'unitsOfMeasure') : this.$tv(lowerUnitOfMeasure, 'unitsOfMeasure')
            title += ' [' + translatedUnitOfMeasure + ']'
            headerTitle = this.$te('product.attributeFullName') ?
              this.$tv({messageKey: 'attributeFullName', messageValues: {fullName: headerTitle, unitOfMeasure: translatedUnitOfMeasure}}, 'product') :
              headerTitle + ' [' + translatedUnitOfMeasure + ']'
          }

          const columnAttributIndexpath = typeof column.Attribute?.IndexPath === 'string' ? column.Attribute.IndexPath : ''
          let translatable = false
          let translatableShort = false
          if (columnAttributIndexpath !== '') {
            const columnAttributIndexpathParts = columnAttributIndexpath.split('/')
            translatable =
              columnAttributIndexpathParts.length === 3 &&
              this.i18n.locale.value in this.i18n.messages.value &&
              this.i18n.messages.value[this.i18n.locale.value]?.attributes &&
              typeof(this.i18n.messages.value[this.i18n.locale.value]?.attributes[columnAttributIndexpathParts[2] + '-values']) === 'object'
            // to translate properties from top level on product 33795
            translatableShort =
              columnAttributIndexpathParts.length === 1 &&
              this.i18n.locale.value in this.i18n.messages.value &&
              this.i18n.messages.value[this.i18n.locale.value]?.attributes &&
              typeof(this.i18n.messages.value[this.i18n.locale.value]?.attributes[columnAttributIndexpath + '-values']) === 'object'
          }

          let newColumn = {
            field: mapProductAttributeNames(column.Attribute.IndexPath),
            title: sanitizeHtml(title, window?.document),
            width: columnAttributIndexpath.toLowerCase() === PROPERTY_SCOPE_FIELDS.BRAND_NAME.toLowerCase() ? 130 : 95,
            sortable: column.Attribute.IsSortable ?? false,
            headerTitle,
            cell: (translatable || translatableShort) ? (translatableShort ? 'propCell' : 'attributeCell') : 'simpleCell',
            translatable: translatable,
            className: sanitizeHtml(title, window?.document).toLowerCase().replace(/\s+/g, '') + '-column',
            locked: (column.IsFixedColumn ?? (column.Attribute.IsFixedColumn ?? false)) || (column.IsFixedBack ?? false),
            isFixedBack: column.IsFixedBack ?? false
          }

          newColumns.push(newColumn)
        })

        // first add in the columns that are fixed and not in the back
        const lockedFront = newColumns.filter(nc => nc.locked && !nc.isFixedBack)
        for (let i = 0; i < lockedFront?.length; i++) {
          tableColumnsCopy.splice(insertIndex, 0, lockedFront[i])
          insertIndex++
        }

        // second add in the columns that are not fixed

        const unlocked = newColumns.filter(nc => !nc.locked)
        for (let i = 0; i < unlocked?.length; i++) {
          tableColumnsCopy.splice(insertIndex, 0, unlocked[i])
          insertIndex++
        }

        // workaround (empty cell without fixed width) to scale the table always to the width of the page
        const tempEmptyCell = {...GRID_SETTINGS.EMPTY_CELL}
        if (columns?.length <= 0) {
          tempEmptyCell.locked = true
        }
        tableColumnsCopy.splice(insertIndex, 0, tempEmptyCell)
        insertIndex++

        // last add locked columns that are isFixedBack
        const lockedBack = newColumns.filter(nc => nc.locked && nc.isFixedBack)
        for (let i = 0; i < lockedBack?.length; i++) {
          tableColumnsCopy.splice(insertIndex, 0, lockedBack[i])
          insertIndex++
        }

        this.tableColumns = tableColumnsCopy
      }

      const sortBy = this.tableColumns.filter(t => t.sortable !== false).map(({field, title}) => ({field, title})).sort((a, b) => a.title?.localeCompare(b.title))
      this.sortByColumns = [{field: '', title: '-'}].concat(sortBy)
    },
    sortChangeHandler: function(e) {
      this.sortByEmitter(e.sort)
    },
    pageChangeHandler: function(event) {
      this.skip = event.page.skip
      this.$emit('changePage', {skip: this.skip, take: this.take})
    },
    pageSizeChangeHandler (event) {
      this.pageSizeDropdownValue = event.value
      this.take = event.value.take
      this.skip = DEFAULT_VALUES.SKIP
      this.$emit('changePage', {skip: this.skip, take: this.take})
    },
    sortByFieldChangeHandler(event) {
      this.sortByField = event.value
    },
    getDetailLinkUrl: function(sku) {
      return this.localePath('/products/' + sku)
    },
    changeSortOrder: function () {
      const field = this.sort[0]?.field ?? null
      if (!field) {
        return
      }

      if (this.isFirstSort && this.isMobile) {
        this.isFirstSort = false
        const sort = [{dir: 'asc', field}]
        this.sortByEmitter(sort)
        return
      }

      const sort = [{dir: (this.sort[0]?.dir === 'asc' ? 'desc' : 'asc'), field}]
      this.sortByEmitter(sort)
    },
    sortByEmitter: function(sort) {
      this.$emit('sortByColumn', sort)
    },
    actionEmitter: function(command, e, product) {
      this.$emit(command, e, product)
    },
    isBuyable(product) {
      const mappedProd = getMappedProduct(product)
      return (((mappedProd?.requestable ?? false) || (mappedProd?.buyable ?? false) || this.isDefaultCustomer) && this.$auth.loggedIn) || !this.$auth.loggedIn
    },
    getQuantityDisplay(quantity, unit) {
      const formattedQuantity = quantity.toLocaleString(this.$i18n.locale === 'en' ? 'en' : 'de')
      return this.$tv({messageKey: 'quantityAndUnit', messageValues: {quantity: formattedQuantity, unit: this.$tv(unit, 'packageUnits')}})
    },
    getGTMItem(item, index) {
      return {
        item_id: item?.sku,
        item_name: item?.locales[this.$i18n.locale],
        currency: null,
        index: (this.skip ?? 0) + index,
        item_brand: item.brandName,
        item_category: item.productDesign,
        item_list_id: this.listId,
        item_list_name: this.listName,
        price: null,
        quantity: null
      }
    },
    trackProductClick(product, index) {
      if (product) {
        let res = this.getGTMItem(product, index)
        this.$gtm && this.$gtm.trackEvent({
          event: 'select_item',
          category: 'product',
          action: 'view_products',
          ecommerce: {value: res.price, items: [res], currency: res.currency}
        })
        this.$emit('clickItem', product?.sku, index)
      }
    },
    saveQuantityBeforeNavigation(product) {
      if (this.quantity !== null && product?.sku) {
        this.quantityStore.saveQuantityForProduct(product.sku, this.quantity)
      }
    },
    async toggleShowInchValues() {
      this.setShowInchValues(!this.showInchValues)
      this.$emit('filterInchValues', this.showInchValues)
    },
    clickItem: function(product, rank) {
      if (!product) {
        return
      }
      this.$emit('clickItem', product?.sku, rank)
    },
    getDescriptionText(field, product) {
      return product[field]
    },
    getSuccessorSku(product) {
      return product?.salesAreas[0]?.successorSku
    },
    onCalculateClick() {
      if (this.showQuantityControls && this.quantity !== null) {
        this.hasCalculatedPrices = true
        this.$emit('calculate', {
          quantity: this.quantity,
          products: this.reducedProducts
        })
      }
    }
  },
  mounted() {
    if (this.showQuantityControls && this.quantity !== null) {
      this.hasCalculatedPrices = true
      this.$emit('calculate', {
        quantity: this.quantity,
        products: this.reducedProducts
      })
    }
  },
  watch: {
    quantity(newVal, oldVal) {
      if (this.showQuantityControls && newVal !== oldVal && this.hasCalculatedPrices) {
        this.hasCalculatedPrices = false
      }
      if (this.showQuantityControls && newVal !== oldVal) {
        this.cleanColumnData()
      }
    },
    columns: {
      immediate: true,
      async handler () {
        this.cleanColumnData()
      }
    },
    showInchValues: {
      handler() {
        this.cleanColumnData()
        this.$emit('filterInchValues', this.showInchValues)
      }
    },
    reducedProducts: {
      immediate: false,
      async handler (list) {
        this.gridKey++
        if (!this.isCcrs && list && list?.length > 0) {
          try {
            this.$gtm && this.$gtm.trackEvent({
              event: 'view_item_list',
              item_list_id: this.listId,
              item_list_name: this.listName,
              ecommerce: {items: this.reducedProducts?.map(this.getGTMItem) || []}
            })
          } catch (_) {
            // never die on tracking error
          }
        }
      }
    }
  }
}
</script>
<style lang="scss">
.product-table-content {
  position: relative;

  .quantity-controls {
    position: absolute;
    top: -3rem;
    right: 1rem;
    padding: 0.75rem 1rem 0.5rem;

    @include breakpoint-down($md) {
      position: static;
      margin-bottom: 1rem;
      padding: 0 !important; // remove padding on mobile per requirement
    }

    &__wrapper {
      display: flex;
      align-items: flex-end;
      gap: 1rem;
      flex-wrap: nowrap;
      min-height: 4.25rem;
      padding: 0;
      position: relative;

      @include breakpoint-down($md) {
        gap: 1.25rem;

        button {
          width: 100%;
          min-width: auto;
        }
      }
    }

    &__input {
      display: flex !important;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.35rem;
      line-height: 1.1;
      width: 13rem;
      flex-shrink: 0;

      @include breakpoint-down($md) {
        width: 11.6875rem;
      }
    }

    &__label {
      font-weight: $base-text-font-weight-light;
      font-size: 0.875rem;
      margin: 0;
      white-space: nowrap;
      overflow: visible;
      max-width: none;
      position: relative;
      left: 0;
      top: 0;
      z-index: 1;
    }

    &__calculate-btn {
      height: 2.5rem;
      display: flex;
      align-items: center;
    }
  }

  .k-grid .k-grid-container .k-grid-content .k-grid-table .k-master-row > td {
    font-size: $base-text-font-size;

    @include breakpoint-down($md) {
      &.availability-column, &.price-column {
        display: flex;
        align-items: center;
        font-size: 0.875rem !important;
      }
    }
  }

  .k-column-title {
    font-size: $base-text-font-size;
  }

  .content__header {
    .loading-indicator {
      position: absolute;
    }
  }

  .loading-indicator {
    padding-left: 2.5rem;
  }

  .grid {
    text-align: center;

    .k-grid {
      position: static;

      @include breakpoint-down($md) {
        .k-grid-pager {
          top: 0;
          background-color: $color__white;
          border: none;
          padding-top: 1rem;
        }

        .k-pager-md {
          padding-inline: 0;
        }

        .k-button.k-selected::before {
            opacity: 0;
        }
      }

      @include breakpoint-down(26.875rem) {
        .k-grid-pager {
          top: $default-margin-padding-width;
          margin-bottom: 0.75rem;
        }
      }

      table {
        tr {
          td {
            &.link-column a {
              display: flex;
              flex-direction: column;
            }

            &:not(.description-column, .action-column) {
              justify-content: flex-start;

              @include breakpoint-down($md) {
                a {
                  color: $color__black;
                  font-size: $base-text-font-size;
                  font-weight: $base-text-font-weight-semi-bold;
                }
              }
            }

            @include breakpoint-up($md) {
              &.hide-desktop {
                display: none;
              }
            }

            &.sku-description-column {
              .sku-field {
                color: $color__primary;
                font-weight: $base-text-font-weight-bold;
                padding-right: .25rem;
                font-size: $base-text-font-size;

              }
            }

            &.price-column {
              div {
                display: flex;
                flex-direction: column;
              }

              .minimum-position-line-value {
                font-size: map-get($base-text-label-small, font-size );
              }

              .price-value {
                font-weight: $base-text-font-weight-semi-bold;
              }

              .price-skeleton {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                margin-bottom: 1.3rem;

                .price-skeleton__heading {
                  min-height: 1rem;
                  width: 4rem;
                }

                .price-skeleton__subtext {
                  min-height: 0.8rem;
                  width:3.5rem;
                  margin-bottom: 1rem;
                }
              }
            }

            &.packaging-column {
              .variants-link {
                color: $color__fst__primary__hover;
                cursor: pointer;
                font-weight: $base-text-font-weight-semi-bold;

                &:hover {
                  text-decoration: underline;
                }
              }

              .packaging-quantity {
                font-weight: $base-text-font-weight-semi-bold;
              }
            }

            &.availability-column {
              >div {
                display: flex;
              }

              .stock-quantity {
                font-weight: $base-text-font-weight-semi-bold;
                padding-left: .2rem;
              }

              .availability-skeleton {
                display: flex;
                align-items: center;

                &__circle {
                  width: .75rem;
                  height: .75rem;
                }

                &__line {
                  flex: 0 0 1.5rem;
                  width: 2rem;
                  height: 0.5rem;
                  margin-left: 0.5rem;
                }
              }

            }

            @include breakpoint-down($md) {
              &.description-column,
              &.action-column,
              &.availability-column,
              &.price-column,
              &.brand-column,
              &.link-column,
              &.brandname-column,
              &.productdesign-column {
                display: flex;
                position: static !important;
              }

              &.action-column {
                margin-bottom: 0;

                &::before {
                  display: none;
                }
              }

              &.description-column {
                text-align: left;
                box-sizing: border-box;
                max-width: calc(100% - 3.5rem);
                margin-bottom: .5rem;
                color: $color__primary;
                font-weight: $base-text-font-weight-bold;
                line-height: 1.5rem;
                font-size: 1.125rem;
                order: 1;

                &::before {
                  display: none;
                }
              }

              .actions {
                color: $color__white;
                display: flex;
                margin-top: 0;
                text-align: center;
                column-gap: .5rem;
                width: 100%;

                .k-button {
                  margin-top: .375rem;
                  min-width: 50%;
                }

                .star .icon--svg {
                  position: absolute;
                  top: .45rem;
                  right: .625rem;
                }

                .shoppingCart {
                  min-width: 50%;

                  .k-button {
                    width: 100%;
                  }

                  .icon--svg {
                    display: none;
                  }
                }
              }
            }
          }

          th {
            @include breakpoint-up($md) {
              &.hide-desktop {
                display: none;
              }
            }

            &.empty-cell {
              text-overflow: clip;
            }
          }

          .actions {
            justify-content: space-between;
            display: flex;

            @include breakpoint-up($md) {
              .button__details {
                display: none;
              }

              .shoppingCart .k-button {
                display: none;
              }
            }

            .icon--svg {
              position: relative;
              top: .2rem;
            }

            .shoppingCart {
              .icon--svg {
                color: $color__primary;
                font-size: 1.7em;
              }

              &.disabled {
                cursor: default;
                pointer-events: none;

                .k-button {
                  background-color: $color__btn__primary--disabled;
                }

                .icon--svg {
                  color: $color__page--background--opacity;
                }
              }
            }

            .star .icon--svg {
              font-size: 1.7em;
              color: $color__page--background--opacity;
            }

            a {
              font-weight: $base-text-font-weight-light;
            }
          }
        }
      }
    }
  }

  &.content {
    padding: 0 $default-margin-padding-2x;

    @include breakpoint-down($md) {
      padding: $default-margin-padding-width;
      background-color: $color__white;
    }

    .content__header {
      display: flex;
      justify-content: space-between;
      padding-right: 1.5em;
      margin-bottom: .9375rem;
      gap: 1rem;

      @include breakpoint-down($md) {
        margin-bottom: 0;
      }

      .content__header-skeleton {
        height: 1.6rem;
        width: 9.5rem;
      }

      h3 {
        height: 1.5625rem;
        margin: 0;

        @include breakpoint-down($md) {
          font-size: 1.0625rem;
          padding: .5rem 0 1rem;
        }
      }

      &__label {
        color: $color__primary;
        padding: .5em 1em;
      }

      &__label:hover {
        cursor: pointer;
      }
    }

    .k-dropdown {
      &::after {
        display: none !important;
      }
    }

    .k-dropdown--yellow {
      font-size: .8125rem;

      @include breakpoint-down($md) {
        display: none !important;
      }
    }

    .inch-value--filter {
      top: -2.75rem;
      right: 12rem;
      position: absolute;
      font-weight: $base-text-font-weight-light;

      > label {
        text-align: left;
      }

      @include breakpoint-up($md) {
        display: none;
      }

      @include breakpoint-down($md) {
        display: block;
        position: relative;
        left: 0;
        top: 0;
        margin-top: 0;
        padding-top: 1.25rem;
        padding-bottom: 1.25rem;
      }
    }

    .sort-by--mobile {
      padding: 1.25rem 0 1.625rem;

      @include breakpoint-up($md) {
        display: none;
      }

      @include breakpoint-down($md) {
        padding-top: 0;
        padding-bottom: $default-margin-padding-width;
        justify-content: flex-start;
        column-gap: 1.25rem;
      }

      &__header {
        flex-basis: 100%;
        font-weight: $base-text-font-weight-light;
        margin-bottom: $default-margin-padding-width-small;
        text-align: left;
      }

      display: flex;
      flex-flow: row wrap;
      justify-content: space-between;

      .sort-by__toggle-btn-group {
        display: inline-flex;

        &--mobile {
          flex-direction: column;
          max-width: 2.5rem;
          max-height: 2.5rem;
          display: none;
          background-color: $color__primary;

          .toggle-button-up, .toggle-button-down {
            display: flex;
            justify-content: center;
            width: 2.5rem;
            height: 1.25rem;
            position: relative;

            &.inactive {
              .icon.icon--svg {
                color: $color__default;
              }
            }
          }

          .toggle-button-up {
            align-items: flex-end;
            top: 0.425rem;
          }

          .toggle-button-down {
            align-items: flex-start;
            bottom: 0.425rem;
          }
        }

        @include breakpoint-down($md) {
          display: none;

          &--mobile {
            display: flex;

            .icon.icon--svg {
              color: $color__white;
              font-size: 1.25rem;
            }
          }
        }
      }

      .sort-by__dropdown__container {
        width: 9.375rem;

        @include breakpoint-down($md) {
          width: 11.6875rem;
        }

        .sort-by__dropdown {
          .k-dropdown {
            height: 2.5rem;
            border-radius: 0;
          }
        }
      }

      .k-button.inactive {
        background-color: $color__toggle-btn--inactive;
      }
    }
  }
}

@include breakpoint-up($md) {
  .inch-value--filter--no-quantity {
    right: 0 !important;
  }

  .product-table-content.content .inch-value--filter--with-quantity {
    right: 26.5rem;
  }
}

.product-table__toggle-switch.toggleSwitch .switch .label {
  width: auto;
  max-width: 6.25rem;
}

.successor-item-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 5rem;
  margin-left: .45rem;
  margin-top: -.5rem;

  &__successor-item {
    font-size: .7rem;
    margin-left: -.69rem;
    text-align: center;
    margin-bottom: .49rem;
    text-decoration: underline;
  }

  @include breakpoint-down($md) {
    display: none;
  }
}

/* Target the Kendo Grid content area specifically */
.grid-wrapper {
  position: relative;

  .price-placeholder {
    display: none !important;
  }

  @include breakpoint-down($md) {
    .price-placeholder {
      display: block !important;
    }
  }
}

/* Overlay text over the price column */
.grid-wrapper.show-price-overlay::before {
  content: attr(data-price-overlay-text);
  position: absolute;
  top: 2.5rem;
  right: 3rem;
  width: 7.1rem;
  padding: .25rem .125rem 0;
  box-sizing: border-box;
  display: block;
  white-space: normal;
  pointer-events: none;
  z-index: 4;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;

  @include breakpoint-down($lg) {
    display: none;
  }
}

/* Clamp to 2 lines with ellipsis when only one row */
.grid-wrapper.show-price-overlay.single-row::before {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
}

/* Full-height background overlay over the price column */
.grid-wrapper.show-price-overlay::after {
  content: '';
  position: absolute;
  top: 2.5rem;
  right: 3rem;
  width: 7.1rem;
  height: calc(100% - 7.2rem);
  background-color: rgb(0 166 226 / 20%);
  pointer-events: none;
  z-index: 3;

  @include breakpoint-down($lg) {
    display: none;
  }
}

.pagination-controls {
  display: flex;
  justify-content: flex-start;
  margin-block: $default-margin-padding-width;

  @include breakpoint-down($md) {
    margin-block: 0;

    &.experimental-position {
      position: absolute;
      bottom: 1.85rem;
      left: .2rem;
      margin: 0;
      z-index: 10;
    }

    .pagination-size-dropdown {
      width: auto;
      min-width: 6.875rem;
      bottom: .375rem;
      left: 0;
    }
  }

  .quantity-controls__numeric-input {
    .k-input-inner {
      font-weight: $base-text-font-weight-light;
    }

    .k-input-spinner {
      .k-button {
        background-color: $color__fst__gray__020;

        .k-icon {
          color: $color__fst_gray__100;
        }

        &:hover {
          background-color: $color__fst__gray__020;

          .k-icon {
            color: $color__fst_gray__100;
          }
        }

        &:active {
          background-color: $color__fst__gray__020;

          .k-icon {
            color: $color__fst_gray__100;
          }
        }
      }
    }
  }

  /* Hide pager sizes dropdown */
  .k-pager-sizes.k-label {
    display: none !important;
  }
}
</style>
